/* eslint max-lines: off */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Hyperlink } from '../../../components/Hyperlink';
import { showEmailOnClick } from '../../lawyer.api';
import { doctrineAnalytics } from '../../../utils/analyticsUtils';
import { getErrorMessage } from '../../../utils/errorMessage';
import { PopoverFactoryRenderer } from '../../../components/popovers/PopoverFactoryRenderer';
import { LawFirmPopover } from '../../../law-firm/popover/LawFirmPopover';
import { HyperlinkRestrictedBaseModal } from '../../../components/HyperlinkRestrictedBaseModal';
import styles from './lawyer_about.module.scss';
import {
  EntityCardContainer,
  EntityCardTitle,
  EntityCardMaterialIcon,
  EntityCardImageIcon,
} from '../../../entity/components/EntityCardComponents';
import { EntityCardItemWithIcon } from '../../../entity/components/EntityCardItemWithIcon';
import { withLawyer } from '../LawyerContext';
import { LawyerContextType } from '../lawyer.types';

type Props = LawyerContextType;

type State = {
  showEmail: boolean;
  lawyerEmail: string;
  errorMessageMail: string;
};

export class RawLawyerAbout extends React.Component<Props, State> {
  state: State = {
    showEmail: false,
    lawyerEmail: '',
    errorMessageMail: '',
  };

  showEmailOnClick = async () => {
    const {
      lawyerInfos: { lawyerId },
    } = this.props;
    doctrineAnalytics('Professional WantToSeeEmail', {
      lawyer_id: lawyerId,
    });
    try {
      const { lawyerEmail } = (await showEmailOnClick(lawyerId)).data;
      if (lawyerEmail) {
        this.setState({
          showEmail: true,
          lawyerEmail,
          errorMessageMail: '',
        });
      }
    } catch (error) {
      if (error.response) {
        this.setState({
          errorMessageMail: getErrorMessage(error.response),
        });
      } else {
        throw error;
      }
    }
  };

  render() {
    const {
      lawyerHasRetired,
      lawyerInfos: {
        postalCode,
        postalCity,
        address,
        languages,
        barreau,
        sermentDate,
        toque,
        website,
        twitter,
        organisation,
        lawFirmRoute,
        linkedin,
        lawOfficeIdGroup,
        canBeContacted,
      },
    } = this.props;
    const mapLink =
      postalCode && postalCity && address
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            `${address},${postalCity} ${postalCode}`,
          )}`
        : null;
    const languagesText = arrayToStringWithComma(languages);
    return (
      <EntityCardContainer id="professional-about">
        <EntityCardTitle>
          <FormattedMessage id="lawyer/page/About.tsx/1" />
        </EntityCardTitle>

        {barreau && (
          <EntityCardItemWithIcon
            icon={<EntityCardMaterialIcon name="school" />}
            subtitle={
              barreau === 'Avocat aux conseils' ? (
                <FormattedMessage id="lawyer/page/About.tsx/14" />
              ) : (
                <FormattedMessage id="lawyer/page/About.tsx/2" />
              )
            }
            text={
              barreau === 'Avocat aux conseils' ? (
                <FormattedMessage id="lawyer/page/About.tsx/15" />
              ) : (
                <span>{barreau}</span>
              )
            }
          />
        )}

        {!lawyerHasRetired &&
          sermentDate &&
          barreau !== 'Avocat aux conseils' && (
            <EntityCardItemWithIcon
              icon={<EntityCardMaterialIcon name="date_range" />}
              subtitle={<FormattedMessage id="lawyer/page/About.tsx/3" />}
              text={sermentDate}
            />
          )}

        {mapLink && (
          <EntityCardItemWithIcon
            icon={<EntityCardMaterialIcon name="location_on" />}
            subtitle={<FormattedMessage id="lawyer/page/About.tsx/4" />}
            text={
              <Hyperlink href={mapLink} className={styles.link}>
                {`${address} ${postalCode} ${postalCity}`}
              </Hyperlink>
            }
          />
        )}

        {languagesText && (
          <EntityCardItemWithIcon
            icon={<EntityCardMaterialIcon name="language" />}
            subtitle={<FormattedMessage id="lawyer/page/About.tsx/5" />}
            text={languagesText}
          />
        )}

        {!lawyerHasRetired && toque && (
          <EntityCardItemWithIcon
            icon={<EntityCardMaterialIcon name="account_circle" />}
            subtitle={<FormattedMessage id="lawyer/page/About.tsx/6" />}
            text={toque}
          />
        )}

        {website && (
          <EntityCardItemWithIcon
            icon={<EntityCardMaterialIcon name="link" />}
            subtitle={<FormattedMessage id="lawyer/page/About.tsx/7" />}
            text={
              <Hyperlink
                className={styles.break}
                href={website}
                target="_blank"
              >
                {website}
              </Hyperlink>
            }
          />
        )}

        {linkedin && (
          <EntityCardItemWithIcon
            icon={
              <EntityCardImageIcon
                src="/img/logo/social/linkedin_clear.png"
                alt="linkedin"
              />
            }
            subtitle={<FormattedMessage id="lawyer/page/About.tsx/8" />}
            text={
              <Hyperlink
                className={styles.break}
                href={linkedin}
                target="_blank"
              >
                <FormattedMessage id="lawyer/page/About.tsx/9" />
              </Hyperlink>
            }
          />
        )}

        {twitter && (
          <EntityCardItemWithIcon
            icon={
              <EntityCardImageIcon
                src="/img/logo/social/twitter_clear.png"
                alt="twitter"
              />
            }
            subtitle={<FormattedMessage id="lawyer/page/About.tsx/10" />}
            text={
              <Hyperlink
                className={styles.break}
                href={twitter}
                target="_blank"
              >
                <FormattedMessage id="lawyer/page/About.tsx/11" />
              </Hyperlink>
            }
          />
        )}

        {canBeContacted && (
          <EntityCardItemWithIcon
            icon={<EntityCardMaterialIcon name="mail_outline" />}
            subtitle={<FormattedMessage id="lawyer/page/About.tsx/12" />}
            text={
              <>
                {!this.state.showEmail && !this.state.errorMessageMail && (
                  <div
                    className={styles.hidden}
                    onClick={this.showEmailOnClick}
                  >
                    <span className={styles.hiddenLabel}>
                      <FormattedMessage id="lawyer/page/About.tsx/16" />
                    </span>
                    <EntityCardMaterialIcon name="keyboard_arrow_down" />
                  </div>
                )}
                {this.state.showEmail && (
                  <Hyperlink
                    className={styles.link}
                    href={`mailto:${this.state.lawyerEmail}`}
                  >
                    {this.state.lawyerEmail}
                  </Hyperlink>
                )}
                {this.state.errorMessageMail && (
                  <span>{this.state.errorMessageMail}</span>
                )}
              </>
            }
          />
        )}

        {organisation && (
          <EntityCardItemWithIcon
            icon={<EntityCardMaterialIcon name="business" />}
            subtitle={<FormattedMessage id="lawyer/page/About.tsx/13" />}
            text={
              <PopoverFactoryRenderer renderTemplate={displayPopover}>
                <HyperlinkRestrictedBaseModal
                  url={lawFirmRoute}
                  className={styles.link}
                  source="lawyer_about"
                >
                  <span data-doctrine-popover data-law-firm={lawOfficeIdGroup}>
                    {organisation}
                  </span>
                </HyperlinkRestrictedBaseModal>
              </PopoverFactoryRenderer>
            }
          />
        )}
      </EntityCardContainer>
    );
  }
}

export const LawyerAbout = withLawyer(RawLawyerAbout);

function arrayToStringWithComma(array: Array<string> | null) {
  if (!array) return null;
  const string = array.join(', ');
  return string;
}

function displayPopover(element: HTMLElement) {
  const lawOfficeIdGroup = element.getAttribute('data-law-firm');
  if (lawOfficeIdGroup) {
    return (
      <LawFirmPopover
        source="lawyer_about_law_firm_popover"
        lawOfficeIdGroup={lawOfficeIdGroup}
      />
    );
  }
}
